// pages/resources/AdvertisingGuide.jsx
import { useState } from 'react';

export default function AdvertisingGuide() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [completedGuides, setCompletedGuides] = useState([]);

  const categories = ['All', 'Beginner', 'Intermediate', 'Advanced'];

  const guides = [
    {
      title: "Getting Started with Digital Advertising",
      duration: "10 min read",
      level: "Beginner",
      topics: ["Basics", "Strategy", "Planning"],
      description: "Learn the fundamentals of digital advertising and how to create your first campaign successfully",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      lessons: 8,
      students: "12.5k"
    },
    {
      title: "Advanced Targeting Strategies",
      duration: "15 min read",
      level: "Advanced",
      topics: ["Targeting", "Optimization", "ROI"],
      description: "Master audience targeting to maximize your advertising ROI and reach the right customers at the right time",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      lessons: 12,
      students: "8.3k"
    },
    {
      title: "Creative Best Practices",
      duration: "12 min read",
      level: "Intermediate",
      topics: ["Creative", "Design", "Conversion"],
      description: "Design compelling ad creatives that capture attention and drive conversions with proven design principles",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        </svg>
      ),
      lessons: 10,
      students: "9.7k"
    },
    {
      title: "Budget Optimization Techniques",
      duration: "18 min read",
      level: "Advanced",
      topics: ["Budget", "Bidding", "CPA"],
      description: "Optimize your ad spend and reduce costs while maintaining campaign performance and scaling results",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      lessons: 15,
      students: "6.9k"
    },
    {
      title: "Multi-Channel Campaign Management",
      duration: "20 min read",
      level: "Advanced",
      topics: ["Multi-Channel", "Integration", "Tracking"],
      description: "Coordinate campaigns across multiple platforms for maximum impact and consistency in your messaging",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
        </svg>
      ),
      lessons: 18,
      students: "5.4k"
    },
    {
      title: "Analytics & Performance Tracking",
      duration: "14 min read",
      level: "Intermediate",
      topics: ["Analytics", "Metrics", "Reporting"],
      description: "Track, measure, and analyze your campaign performance with key metrics and data-driven insights",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      lessons: 11,
      students: "10.2k"
    },
    {
      title: "A/B Testing Fundamentals",
      duration: "13 min read",
      level: "Beginner",
      topics: ["Testing", "Experimentation", "Data"],
      description: "Learn how to run effective A/B tests to improve campaign performance and make data-driven decisions",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
      lessons: 9,
      students: "11.8k"
    },
    {
      title: "Conversion Rate Optimization",
      duration: "16 min read",
      level: "Intermediate",
      topics: ["CRO", "Landing Pages", "UX"],
      description: "Increase conversions by optimizing your landing pages, user experience, and conversion funnels",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
      lessons: 13,
      students: "7.6k"
    }
  ];

  const filteredGuides = activeCategory === 'All' 
    ? guides 
    : guides.filter(guide => guide.level === activeCategory);

  const toggleComplete = (index) => {
    if (completedGuides.includes(index)) {
      setCompletedGuides(completedGuides.filter(i => i !== index));
    } else {
      setCompletedGuides([...completedGuides, index]);
    }
  };

  const progressPercentage = ((completedGuides.length / guides.length) * 100).toFixed(0);

  const getLevelColor = (level) => {
    switch(level) {
      case 'Beginner': return 'from-green-500 to-green-600';
      case 'Intermediate': return 'from-yellow-500 to-yellow-600';
      case 'Advanced': return 'from-red-500 to-red-600';
      default: return 'from-[#00809D] to-[#00a0c4]';
    }
  };

  const getLevelBadgeColor = (level) => {
    switch(level) {
      case 'Beginner': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'Intermediate': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'Advanced': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-[#00809D]/20 text-[#00809D] border-[#00809D]/30';
    }
  };

  return (
    <div className="min-h-screen bg-black pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-2xl mb-6 shadow-2xl shadow-[#00809D]/50">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-4">
            Digital Advertising Guide
          </h1>
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto">
            Master digital advertising with our comprehensive step-by-step guides
          </p>
        </div>

        {/* Progress Card */}
        <div className="mb-12 bg-gradient-to-br from-[#00809D]/10 to-[#00a0c4]/5 border border-[#00809D]/30 rounded-3xl p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex-1">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Your Learning Progress</h3>
              <p className="text-gray-400 mb-4">
                {completedGuides.length} of {guides.length} guides completed
              </p>
              <div className="w-full bg-gray-800 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-[#00809D] to-[#00a0c4] h-3 rounded-full transition-all duration-500 relative"
                  style={{ width: `${progressPercentage}%` }}
                >
                  <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-2xl flex items-center justify-center text-white font-bold text-2xl sm:text-3xl shadow-lg shadow-[#00809D]/50">
                  {progressPercentage}%
                </div>
                <p className="text-gray-400 text-xs sm:text-sm mt-2">Complete</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-4 sm:p-6 hover:border-[#00809D]/50 transition-all">
            <div className="flex items-center gap-3 mb-2">
              <svg className="w-8 h-8 text-[#00809D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <p className="text-2xl sm:text-3xl font-bold text-white">{guides.length}</p>
            </div>
            <p className="text-gray-400 text-xs sm:text-sm">Total Guides</p>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-4 sm:p-6 hover:border-[#00809D]/50 transition-all">
            <div className="flex items-center gap-3 mb-2">
              <svg className="w-8 h-8 text-[#00809D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-2xl sm:text-3xl font-bold text-white">2-20</p>
            </div>
            <p className="text-gray-400 text-xs sm:text-sm">Minutes Each</p>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-4 sm:p-6 hover:border-[#00809D]/50 transition-all">
            <div className="flex items-center gap-3 mb-2">
              <svg className="w-8 h-8 text-[#00809D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-2xl sm:text-3xl font-bold text-white">62k+</p>
            </div>
            <p className="text-gray-400 text-xs sm:text-sm">Students</p>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-4 sm:p-6 hover:border-[#00809D]/50 transition-all">
            <div className="flex items-center gap-3 mb-2">
              <svg className="w-8 h-8 text-[#00809D]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              <p className="text-2xl sm:text-3xl font-bold text-white">4.9</p>
            </div>
            <p className="text-gray-400 text-xs sm:text-sm">Avg Rating</p>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-medium transition-all text-sm sm:text-base ${
                  activeCategory === category
                    ? 'bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white shadow-lg shadow-[#00809D]/50 scale-105'
                    : 'bg-gray-900 text-gray-400 border border-white/10 hover:border-[#00809D]/50 hover:text-white hover:scale-105'
                }`}
              >
                {category}
                {category !== 'All' && (
                  <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                    {guides.filter(g => g.level === category).length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Guides List */}
        <div className="space-y-6">
          {filteredGuides.map((guide, index) => {
            const isCompleted = completedGuides.includes(index);
            
            return (
              <div
                key={index}
                className={`group bg-gradient-to-r from-gray-900 to-black border rounded-3xl p-6 sm:p-8 transition-all duration-300 hover:shadow-2xl ${
                  isCompleted 
                    ? 'border-green-500/50 shadow-lg shadow-green-500/20' 
                    : 'border-white/10 hover:border-[#00809D]/50 hover:shadow-[#00809D]/20'
                }`}
              >
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-16 h-16 bg-gradient-to-br ${getLevelColor(guide.level)} rounded-2xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-all`}>
                    {guide.icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 bg-[#00809D] rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {index + 1}
                        </span>
                        <h3 className="text-xl sm:text-2xl font-bold text-white group-hover:text-[#00809D] transition-colors">
                          {guide.title}
                        </h3>
                      </div>
                      <span className={`px-3 py-1 ${getLevelBadgeColor(guide.level)} rounded-full text-xs sm:text-sm font-semibold border`}>
                        {guide.level}
                      </span>
                    </div>

                    <p className="text-gray-400 mb-4 text-sm sm:text-base leading-relaxed">
                      {guide.description}
                    </p>

                    {/* Meta Info */}
                    <div className="flex flex-wrap items-center gap-4 sm:gap-6 mb-4 text-xs sm:text-sm text-gray-400">
                      <span className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {guide.duration}
                      </span>
                      <span className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                        {guide.lessons} Lessons
                      </span>
                      <span className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        {guide.students} students
                      </span>
                    </div>

                    {/* Topics */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {guide.topics.map((topic, i) => (
                        <span key={i} className="px-3 py-1 bg-[#00809D]/20 text-[#00809D] rounded-full text-xs sm:text-sm font-medium">
                          {topic}
                        </span>
                      ))}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button className="flex-1 bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all shadow-lg shadow-[#00809D]/50 flex items-center justify-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                        {isCompleted ? 'Read Again' : 'Start Reading'}
                      </button>
                      <button
                        onClick={() => toggleComplete(index)}
                        className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                          isCompleted
                            ? 'bg-green-500/20 text-green-400 border-2 border-green-500/50 hover:bg-green-500/30'
                            : 'bg-gray-800 text-gray-400 border-2 border-white/10 hover:border-[#00809D]/50 hover:text-white'
                        }`}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {isCompleted ? 'Completed' : 'Mark Complete'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-[#00809D]/10 to-transparent border border-[#00809D]/30 rounded-3xl p-8 sm:p-12">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Ready to Start Learning?
            </h3>
            <p className="text-gray-400 mb-8 text-base sm:text-lg">
              Join thousands of marketers mastering digital advertising with our comprehensive guides
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-8 py-4 rounded-xl font-semibold hover:scale-105 transition-all shadow-lg shadow-[#00809D]/50 flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                Start First Guide
              </button>
              <button className="bg-gray-800 text-white px-8 py-4 rounded-xl font-semibold hover:bg-gray-700 transition-all flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
                Download All Guides
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
